/**
 * AnonymousLabUploadPage — privacy-first leadgen tool.
 *
 * Allows ANY visitor (no signup) to upload a lab PDF/image and get an
 * educational AI analysis within 30-60 seconds. Result auto-deletes after
 * 24 hours via MongoDB TTL index. Compliant with GDPR Art. 5 (data
 * minimization) + Art. 17 (right to erasure).
 */
import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import {
  Upload, FileText, Clock, Shield, AlertCircle, CheckCircle2, Trash2,
  Loader2, Sparkles, ArrowRight, Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TX = {
  bg: {
    seo_title: "Анонимен анализ на лабораторно изследване",
    seo_desc: "Качи лабораторен резултат без регистрация. Получи образователен AI преглед. Данните се изтриват автоматично след 24 часа.",
    back: "Начало",
    badge: "Без регистрация · Без кредитна карта",
    title: "Анонимен анализ на лабораторно изследване",
    subtitle: "Качи PDF или снимка на лабораторния си резултат. Получаваш образователен преглед в рамките на 30–60 секунди. Данните се изтриват автоматично след 24 часа.",
    privacy_t: "Защита на данните",
    privacy: [
      "Без регистрация и без съхраняване на лични данни",
      "Файлът остава на нашия сървър максимум 24 часа",
      "Можеш да изтриеш анализа си по всяко време",
      "Хостинг в Европейския съюз · криптиране AES-256",
    ],
    upload_t: "Качи файл",
    upload_hint: "PDF, JPG, PNG · до 10 MB",
    drop_here: "Пусни файла тук или кликни за избор",
    consent_label: "Разбирам, че това е образователен инструмент и не замества медицинска консултация.",
    submit: "Започни анализ",
    submitting: "Изпращане…",
    processing: "Анализиране на резултата…",
    processing_hint: "Това отнема около 30–60 секунди.",
    result_t: "Резултат от анализа",
    summary: "Обобщение",
    findings: "Маркери",
    suggestions: "Образователни предложения",
    next_steps: "Следващи стъпки",
    disclaimer: "Този анализ е само образователен. Не замества медицинска консултация. Резултатите варират според индивида.",
    expires_in: "Изтича след",
    delete: "Изтрий моите данни сега",
    deleted: "Анализът е изтрит. Освежи страницата, за да започнеш отначало.",
    error_invalid: "Невалиден файл. Поддържат се PDF, JPG, PNG, WEBP до 10 MB.",
    error_consent: "Моля, потвърди че разбираш образователния характер на инструмента.",
    error_generic: "Възникна грешка. Опитай отново.",
    upgrade: "Искаш по-задълбочен анализ?",
    upgrade_btn: "Регистрирай се безплатно",
  },
  en: {
    seo_title: "Anonymous lab report analysis",
    seo_desc: "Upload a lab result without signing up. Get an educational AI review. Data auto-deletes after 24 hours.",
    back: "Home",
    badge: "No signup · No credit card",
    title: "Anonymous lab report analysis",
    subtitle: "Upload a PDF or image of your lab result. You'll get an educational review within 30–60 seconds. Data auto-deletes after 24 hours.",
    privacy_t: "Data protection",
    privacy: [
      "No signup, no personal data stored",
      "File stays on our server for up to 24 hours",
      "You can delete your analysis at any time",
      "EU-based hosting · AES-256 encryption",
    ],
    upload_t: "Upload file",
    upload_hint: "PDF, JPG, PNG · up to 10 MB",
    drop_here: "Drop file here or click to select",
    consent_label: "I understand this is an educational tool and not a substitute for medical consultation.",
    submit: "Start analysis",
    submitting: "Uploading…",
    processing: "Analyzing your result…",
    processing_hint: "This takes about 30–60 seconds.",
    result_t: "Analysis result",
    summary: "Summary",
    findings: "Markers",
    suggestions: "Educational suggestions",
    next_steps: "Next steps",
    disclaimer: "This is educational only. Not a substitute for medical consultation. Results vary by individual.",
    expires_in: "Expires in",
    delete: "Delete my data now",
    deleted: "Your analysis has been deleted. Refresh to start over.",
    error_invalid: "Invalid file. PDF, JPG, PNG, WEBP up to 10 MB supported.",
    error_consent: "Please confirm you understand the educational nature of this tool.",
    error_generic: "Something went wrong. Please try again.",
    upgrade: "Want a deeper analysis?",
    upgrade_btn: "Sign up free",
  },
  es: {
    seo_title: "Análisis anónimo de resultados de laboratorio",
    seo_desc: "Sube un resultado de laboratorio sin registrarte. Obtén una revisión educativa con AI. Los datos se eliminan automáticamente después de 24 horas.",
    back: "Inicio",
    badge: "Sin registro · Sin tarjeta de crédito",
    title: "Análisis anónimo de resultados de laboratorio",
    subtitle: "Sube un PDF o una imagen de tu resultado de laboratorio. Recibirás una revisión educativa en 30–60 segundos. Los datos se eliminan automáticamente después de 24 horas.",
    privacy_t: "Protección de datos",
    privacy: [
      "Sin registro, sin datos personales almacenados",
      "El archivo permanece en nuestro servidor hasta 24 horas",
      "Puedes eliminar tu análisis en cualquier momento",
      "Alojamiento en la UE · Cifrado AES-256",
    ],
    upload_t: "Subir archivo",
    upload_hint: "PDF, JPG, PNG · hasta 10 MB",
    drop_here: "Suelta el archivo aquí o haz clic para seleccionar",
    consent_label: "Entiendo que esta es una herramienta educativa y no un sustituto de la consulta médica.",
    submit: "Iniciar análisis",
    submitting: "Subiendo…",
    processing: "Analizando tu resultado…",
    processing_hint: "Esto tarda aproximadamente 30–60 segundos.",
    result_t: "Resultado del análisis",
    summary: "Resumen",
    findings: "Marcadores",
    suggestions: "Sugerencias educativas",
    next_steps: "Próximos pasos",
    disclaimer: "Esto es solo educativo. No sustituye la consulta médica. Los resultados varían según cada persona.",
    expires_in: "Expira en",
    delete: "Eliminar mis datos ahora",
    deleted: "Tu análisis ha sido eliminado. Actualiza la página para empezar de nuevo.",
    error_invalid: "Archivo no válido. Se admiten PDF, JPG, PNG, WEBP de hasta 10 MB.",
    error_consent: "Por favor, confirma que entiendes la naturaleza educativa de esta herramienta.",
    error_generic: "Algo salió mal. Por favor, inténtalo de nuevo.",
    upgrade: "¿Quieres un análisis más detallado?",
    upgrade_btn: "Regístrate gratis",
  },
};

export default function AnonymousLabUploadPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language] || TX.en;

  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [taskId, setTaskId] = useState(null);
  const [status, setStatus] = useState("idle"); // idle, uploading, processing, ready, failed, deleted
  const [analysis, setAnalysis] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [countdown, setCountdown] = useState("");
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // Countdown for TTL
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const ms = new Date(expiresAt).getTime() - Date.now();
      if (ms <= 0) {
        setCountdown("00:00:00");
        return;
      }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setCountdown(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  // Polling
  useEffect(() => {
    if (!taskId || status !== "processing") return;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/anonymous/lab-analysis/${taskId}`);
        if (!r.ok) return;
        const d = await r.json();
        if (d.status === "ready") {
          setAnalysis(d.analysis);
          setStatus("ready");
        } else if (d.status === "failed") {
          setError(d.error || t.error_generic);
          setStatus("failed");
        }
      } catch {
        // ignore — keep polling
      }
    };
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [taskId, status, t.error_generic]);

  const onPickFile = (f) => {
    setError("");
    if (!f) return;
    const allowed = [".pdf", ".jpg", ".jpeg", ".png", ".webp"];
    const ok = allowed.some((ext) => f.name.toLowerCase().endsWith(ext));
    if (!ok || f.size > 10 * 1024 * 1024) {
      setError(t.error_invalid);
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    onPickFile(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (!file) {
      setError(t.error_invalid);
      return;
    }
    if (!consent) {
      setError(t.error_consent);
      return;
    }
    setError("");
    setStatus("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/anonymous/lab-analysis`, {
        method: "POST",
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) {
        setError(d.detail || t.error_generic);
        setStatus("failed");
        return;
      }
      setTaskId(d.task_id);
      setExpiresAt(d.expires_at);
      setStatus("processing");
    } catch (e) {
      setError(t.error_generic);
      setStatus("failed");
    }
  };

  const remove = async () => {
    if (!taskId) return;
    try {
      await fetch(`${API}/anonymous/lab-analysis/${taskId}`, { method: "DELETE" });
    } catch {
      // ignore
    }
    setStatus("deleted");
    setAnalysis(null);
    setFile(null);
  };

  const sectionBg = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-[#F9F9F7]";
  const cardBg = isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm";

  return (
    <div className={`min-h-screen ${sectionBg}`} data-testid="anonymous-lab-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <SEO title={t.seo_title} description={t.seo_desc} path="/anonymous-lab" />
      <SubpageNav />

      <section className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <SubpageBackLink label={t.back} />

        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium mb-4 ${isDark ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10" : "border-emerald-200 text-emerald-700 bg-emerald-50"}`}>
          <Lock className="w-3.5 h-3.5" /> {t.badge}
        </div>
        <h1 className={`text-3xl sm:text-4xl font-bold tracking-tight mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
          {t.title}
        </h1>
        <p className={`text-base leading-relaxed mb-8 max-w-2xl ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          {t.subtitle}
        </p>

        {/* Privacy panel */}
        <div className={`rounded-2xl border p-5 mb-8 ${cardBg}`} data-testid="anon-privacy-panel">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-5 h-5 text-emerald-500" />
            <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.privacy_t}</p>
          </div>
          <ul className="space-y-2">
            {t.privacy.map((p) => (
              <li key={p} className="flex items-start gap-2 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-500" />
                <span className={isDark ? "text-slate-300" : "text-slate-700"}>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* IDLE state — upload form */}
        {(status === "idle" || status === "failed" || status === "uploading") && (
          <div className={`rounded-2xl border p-6 ${cardBg}`} data-testid="anon-upload-form">
            <p className={`text-sm font-bold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.upload_t}</p>

            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors mb-4 ${
                isDark ? "border-slate-700 hover:border-emerald-500/50 bg-[hsl(223,47%,12%)]" : "border-slate-300 hover:border-emerald-400 bg-slate-50"
              }`}
              data-testid="anon-dropzone"
            >
              <Upload className={`w-10 h-10 mx-auto mb-3 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
              <p className={`text-sm font-medium mb-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {file ? file.name : t.drop_here}
              </p>
              <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.upload_hint}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => onPickFile(e.target.files?.[0])}
                className="hidden"
                data-testid="anon-file-input"
              />
            </div>

            <label className="flex items-start gap-2 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 cursor-pointer accent-emerald-500"
                data-testid="anon-consent-checkbox"
              />
              <span className={`text-xs leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {t.consent_label}
              </span>
            </label>

            {error && (
              <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="anon-error">
                <AlertCircle className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-500">{error}</p>
              </div>
            )}

            <Button
              onClick={submit}
              disabled={status === "uploading"}
              data-testid="anon-submit-btn"
              className="w-full bg-gradient-to-r from-emerald-500 to-sky-500 hover:opacity-90 text-white text-sm font-semibold rounded-full h-12"
            >
              {status === "uploading" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.submitting}
                </>
              ) : (
                <>
                  {t.submit} <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* PROCESSING state */}
        {status === "processing" && (
          <div className={`rounded-2xl border p-10 text-center ${cardBg}`} data-testid="anon-processing">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-sky-500" />
            <p className={`text-sm font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.processing}</p>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.processing_hint}</p>
          </div>
        )}

        {/* READY state */}
        {status === "ready" && analysis && (
          <div className="space-y-6" data-testid="anon-result">
            {/* Header strip */}
            <div className={`flex items-center justify-between rounded-2xl border p-4 ${cardBg}`}>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.result_t}</p>
              </div>
              {countdown && (
                <div className="flex items-center gap-1.5 text-xs">
                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                  <span className={isDark ? "text-slate-300" : "text-slate-700"}>
                    {t.expires_in} <span className="font-mono font-bold">{countdown}</span>
                  </span>
                </div>
              )}
            </div>

            {/* Analysis content */}
            <div className={`rounded-2xl border p-6 space-y-5 ${cardBg}`}>
              {analysis.title && (
                <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{analysis.title}</h2>
              )}
              {analysis.summary && (
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{t.summary}</p>
                  <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>{analysis.summary}</p>
                </div>
              )}
              {Array.isArray(analysis.findings) && analysis.findings.length > 0 && (
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{t.findings}</p>
                  <div className="space-y-2">
                    {analysis.findings.map((f, i) => (
                      <div key={i} className={`flex items-start justify-between gap-3 p-3 rounded-lg ${isDark ? "bg-[hsl(223,47%,12%)]" : "bg-slate-50"}`}>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{f.marker}</p>
                          {f.explanation && (
                            <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>{f.explanation}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end text-xs flex-shrink-0">
                          <span className={`font-bold ${
                            f.status === "high" || f.status === "critical" ? "text-red-500"
                            : f.status === "low" ? "text-amber-500"
                            : "text-emerald-500"
                          }`}>{f.value}</span>
                          {f.reference && <span className={isDark ? "text-slate-500" : "text-slate-400"}>{f.reference}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(analysis.lifestyle_suggestions) && analysis.lifestyle_suggestions.length > 0 && (
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{t.suggestions}</p>
                  <ul className="space-y-1.5">
                    {analysis.lifestyle_suggestions.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Sparkles className="w-3.5 h-3.5 mt-1 text-emerald-500 flex-shrink-0" />
                        <span className={isDark ? "text-slate-300" : "text-slate-700"}>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.next_steps && (
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>{t.next_steps}</p>
                  <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>{analysis.next_steps}</p>
                </div>
              )}

              {/* Disclaimer */}
              <div className={`flex items-start gap-2 pt-4 border-t ${isDark ? "border-[hsl(223,47%,16%)]" : "border-slate-100"}`}>
                <FileText className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
                <p className={`text-[11px] leading-relaxed ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                  {t.disclaimer}
                </p>
              </div>
            </div>

            {/* Delete button */}
            <Button
              onClick={remove}
              variant="outline"
              data-testid="anon-delete-btn"
              className={`w-full h-11 rounded-full text-sm font-medium ${isDark ? "border-slate-700 text-slate-300" : "border-slate-300 text-slate-700"}`}
            >
              <Trash2 className="w-4 h-4 mr-2" /> {t.delete}
            </Button>

            {/* Upgrade CTA */}
            <div className={`rounded-2xl p-6 text-center ${isDark ? "bg-gradient-to-br from-sky-500/10 to-emerald-500/10 border border-sky-500/30" : "bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-200"}`}>
              <p className={`text-sm font-bold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.upgrade}</p>
              <Link to="/auth">
                <Button data-testid="anon-upgrade-btn" className="bg-gradient-to-r from-sky-500 to-emerald-500 text-white text-sm font-semibold rounded-full h-11 px-7">
                  {t.upgrade_btn} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* DELETED state */}
        {status === "deleted" && (
          <div className={`rounded-2xl border p-10 text-center ${cardBg}`} data-testid="anon-deleted">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-700"}`}>{t.deleted}</p>
          </div>
        )}
      </section>

      <SubpageFooter />
    </div>
  );
}
